/**
 * ปุ่มสลับ ☀︎/☾ มุมขวาบนของ #orderHeader — เฉพาะธีม glass (spec §B3)
 * แตะ = สลับเป็นโหมดตรงข้าม + จำ (oc-scheme) · กดค้าง ≥ 600ms = ล้างค่าที่เลือก กลับอัตโนมัติ (เวลา + เครื่อง)
 * คีย์บอร์ด: Enter/Space กดสั้น = แตะ · กดค้าง ≥ 600ms = กดค้าง
 */
import { useEffect, useRef, useState, type KeyboardEvent, type MouseEvent, type PointerEvent } from 'react'
import {
  applySchemeWithTransition,
  clearUserScheme,
  getScheme,
  setUserScheme,
  useColorScheme,
} from '../../lib/colorScheme'

const LONG_PRESS_MS = 600
const TOAST_MS = 1800

export function SchemeToggle() {
  const { scheme, auto } = useColorScheme()
  const [toast, setToast] = useState({ msg: '', on: false })
  const [spin, setSpin] = useState(0) // เปลี่ยน key → ไอคอน mount ใหม่ → หมุน 1 รอบ
  const press = useRef<number | undefined>(undefined)
  const longDone = useRef(false)
  const keyHeld = useRef(false)
  const toastT = useRef<number | undefined>(undefined)

  useEffect(
    () => () => {
      clearTimeout(press.current)
      clearTimeout(toastT.current)
    },
    [],
  )

  const showToast = (msg: string) => {
    setToast({ msg, on: true })
    clearTimeout(toastT.current)
    toastT.current = window.setTimeout(() => setToast((t) => ({ ...t, on: false })), TOAST_MS)
  }

  const cancelPress = () => clearTimeout(press.current)
  /** ทิ้งสถานะ "เพิ่งกดค้าง" — ไม่งั้นการกดด้วยคีย์บอร์ดครั้งถัดไปถูกกลืน */
  const resetPress = () => {
    cancelPress()
    longDone.current = false
    keyHeld.current = false
  }
  // นิ้ว/ปากกา: pointerleave มาหลัง pointerup แต่ก่อน click → ล้างเฉพาะเมาส์ (click ที่ตามหลังการกดค้างยังถูกข้าม)
  const onPointerLeave = (e: PointerEvent) => (e.pointerType === 'mouse' ? resetPress() : cancelPress())

  const startPress = () => {
    longDone.current = false
    cancelPress()
    press.current = window.setTimeout(() => {
      longDone.current = true
      applySchemeWithTransition(clearUserScheme)
      showToast('กลับเป็นอัตโนมัติ (ตามเวลา + เครื่อง)')
    }, LONG_PRESS_MS)
  }

  const toggle = () => {
    const next = getScheme() === 'dark' ? 'light' : 'dark'
    applySchemeWithTransition(() => {
      const saved = setUserScheme(next) // บันทึกไม่ได้ → ไม่บอกว่า «จำไว้แล้ว»
      showToast(`${next === 'dark' ? '☾ โหมดมืด' : '☀︎ โหมดสว่าง'}${saved ? ' · จำไว้แล้ว' : ''}`)
    })
    setSpin((n) => n + 1)
  }

  const onPointerDown = (e: PointerEvent) => {
    if (e.button !== 0) return
    startPress()
  }

  // Enter/Space จัดการเอง (preventDefault ทั้ง keydown/keyup = ไม่มี click ตามมา) — Enter ปกติคลิกตั้งแต่ keydown และซ้ำตาม auto-repeat
  const isActKey = (e: KeyboardEvent) => e.key === 'Enter' || e.key === ' '
  const onKeyDown = (e: KeyboardEvent) => {
    if (!isActKey(e)) return
    e.preventDefault()
    if (e.repeat || keyHeld.current) return // จับเวลาครั้งเดียวต่อการกด
    keyHeld.current = true
    startPress()
  }
  const onKeyUp = (e: KeyboardEvent) => {
    if (!isActKey(e) || !keyHeld.current) return
    e.preventDefault()
    keyHeld.current = false
    cancelPress()
    if (longDone.current) longDone.current = false // กดค้างทำงานไปแล้ว — ไม่สลับซ้ำ
    else toggle()
  }

  const onClick = (e: MouseEvent) => {
    cancelPress()
    if (e.detail === 0) longDone.current = false // click จากโปรแกรมอ่านจอ/สคริปต์ — ไม่ใช่ click ที่ตามหลังการกดค้าง
    if (longDone.current) {
      longDone.current = false // คลิกที่ตามหลังการกดค้าง — ไม่สลับซ้ำ
      return
    }
    toggle()
  }

  const now = `${scheme === 'dark' ? 'โหมดมืด' : 'โหมดสว่าง'}${auto ? ' · อัตโนมัติ' : ''}`

  // toast เป็นพี่น้องของปุ่ม (ไม่ใช่ลูก) — ปุ่มขยายตอนกด จะได้ไม่ลากตำแหน่ง fixed ไปด้วย
  return (
    <>
      <button
        type="button"
        className="oc-scheme-btn"
        data-scheme-toggle=""
        aria-label={`สลับโหมดมืด/สว่าง (ตอนนี้: ${now}) · กดค้างเพื่อกลับอัตโนมัติ`}
        onPointerDown={onPointerDown}
        onKeyDown={onKeyDown}
        onKeyUp={onKeyUp}
        onPointerUp={cancelPress}
        onPointerLeave={onPointerLeave}
        onPointerCancel={resetPress}
        onBlur={resetPress}
        onContextMenu={(e) => e.preventDefault()}
        onClick={onClick}
      >
        <i key={spin} className={`fas ${scheme === 'dark' ? 'fa-moon' : 'fa-sun'}${spin ? ' is-spin' : ''}`} aria-hidden="true" />
        {auto && <span className="oc-scheme-auto">A</span>}
      </button>
      <span className={`oc-scheme-toast${toast.on ? ' is-on' : ''}`} role="status" aria-live="polite">
        {toast.msg}
      </span>
    </>
  )
}
